import React, { useState } from "react";
import {
  Suggestion,
  suggestionsApi,
  UpdateSuggestionRequest,
} from "api/suggestions";
import {
  getSuggestionTypeTranslationKey,
  handleAxiosAndCaughtError,
} from "utils";
import toast from "react-hot-toast";
import { useAuthContext } from "auth/AuthProvider";
import { PERMISSIONS } from "auth/permissions";
import { useTranslation } from "react-i18next";

interface EditSuggestionFormProps {
  onClose: () => void;
  onSuccess: () => Promise<void>;
  suggestion: Suggestion;
  agentId?: string;
}

const EditSuggestionForm: React.FC<EditSuggestionFormProps> = ({
  onClose,
  onSuccess,
  suggestion,
  agentId,
}) => {
  const { t } = useTranslation();
  const [suggestionContent, setSuggestionContent] = useState(
    suggestion.suggestion_content
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { hasPermission } = useAuthContext();

  const canUpdateSuggestions =
    hasPermission(PERMISSIONS.UPDATE_SUGGESTIONS) ||
    hasPermission(PERMISSIONS.SUPER_ADMIN);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!canUpdateSuggestions) {
      toast.error(t("suggestions.noPermissionToUpdate"));
      return;
    }

    if (!agentId) {
      toast.error(t("suggestions.agentIdRequired"));
      return;
    }

    if (!suggestionContent.trim()) {
      toast.error(t("suggestions.contentRequired"));
      return;
    }

    setIsSubmitting(true);
    try {
      const data: UpdateSuggestionRequest = {
        suggestion_content: suggestionContent.trim(),
        type: suggestion.type, // Use original type, don't allow changing
      };

      await suggestionsApi.update(agentId, suggestion.id, data);
      toast.success(t("suggestions.updatedSuccessfully"));
      await onSuccess();
      onClose();
    } catch (err) {
      handleAxiosAndCaughtError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex max-h-[70vh] flex-col gap-6 p-2">
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t("suggestions.editSuggestion")}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t("suggestions.updateDescription")}
          </p>
        </div>

        <div className="flex flex-col gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t("suggestions.suggestionContent")}
            </label>
            <textarea
              rows={6}
              className="mt-2 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm placeholder-gray-400 shadow-sm transition-colors focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500"
              value={suggestionContent}
              onChange={(e) => setSuggestionContent(e.target.value)}
              required
              placeholder={t("suggestions.enterSuggestionContent")}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {t("suggestions.suggestionAvailable")}
            </p>
          </div>

          <div className="rounded-lg bg-blue-50 p-4 dark:bg-blue-900/20">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-blue-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  {t("suggestions.suggestionTypeLabel")}
                </h3>
                <div className="mt-2 text-sm text-blue-700 dark:text-blue-300">
                  <p>
                    <strong>{t("suggestions.typeLabel")}</strong>{" "}
                    {t(getSuggestionTypeTranslationKey(suggestion.type))}
                  </p>
                  <p className="mt-1 text-xs text-blue-600 dark:text-blue-400">
                    {t("suggestions.cannotChangeType")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-6">
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
        >
          {t("common.cancel")}
        </button>
        <button
          type="submit"
          onClick={handleSubmit}
          className="rounded-lg bg-brand-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={isSubmitting || !suggestionContent.trim()}
        >
          {isSubmitting ? (
            <div className="flex items-center gap-2">
              <div className="border-t-transparent h-4 w-4 animate-spin rounded-full border-2 border-white" />
              {t("suggestions.updating")}
            </div>
          ) : (
            t("suggestions.updateSuggestion")
          )}
        </button>
      </div>
    </div>
  );
};

export default EditSuggestionForm;
